import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {BoundariesZone as BoundariesZoneType} from "../../../components/types";
import AreaZone, {SoundFileDef} from "./AreaZone";
import {Box, ExpandedIndex, Select, Text} from "@chakra-ui/react";
import ManagedCheckBox from "../../../components/form/ManagedCheckBox";
import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import {ZoneContext, ZoneContextI} from "../../../components/context/ZoneContextWrapper";
import HelpTooltip from "../../../components/help/HelpTooltip";

type Props = {
    zone?: BoundariesZoneType,
    active: boolean
    initialExpandedIndex?: ExpandedIndex
    onExpandedIndexChange?: (index: ExpandedIndex) => void
}

const BoundariesZone = ({zone, active, initialExpandedIndex, onExpandedIndexChange}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const zoneContext: ZoneContextI = useContext<ZoneContextI>(ZoneContext);
    const bleEnabled = appContext.isBleEnabled();
    const zoneRef = useRef(zone);
    const updateZoneRef = useRef(appContext.updateZone);
    const setSelectedZoneRef = useRef(zoneContext.setSelectedZone);
    zoneRef.current = zone;
    updateZoneRef.current = appContext.updateZone;
    setSelectedZoneRef.current = zoneContext.setSelectedZone;

    const [triggerToActivate, setTriggerToActivate] = useState<boolean>(zone?.triggerToActivate ?? false);
    const [beaconSensitivity, setBeaconSensitivity] = useState<number>(zone?.beaconSensitivity ?? 8);

    useEffect(() => {
        if (active) {
            setSelectedZoneRef.current(zone?.id);
        }
    }, [active, zone?.id]);

    useEffect(() => {
        setTriggerToActivate(zone?.triggerToActivate ?? false);
    }, [zone?.id, zone?.triggerToActivate]);

    useEffect(() => {
        const currentZone = zoneRef.current;
        if (triggerToActivate !== (currentZone?.triggerToActivate ?? false)) {
            updateZoneRef.current(currentZone, {
                ...currentZone,
                triggerToActivate: triggerToActivate
            } as BoundariesZoneType)
        }
    }, [triggerToActivate]);

    useEffect(() => {
        setBeaconSensitivity(zone?.beaconSensitivity ?? 8);
    }, [zone?.id, zone?.beaconSensitivity]);

    useEffect(() => {
        const currentZone = zoneRef.current;
        const currentSensitivity = currentZone?.beaconSensitivity ?? 8;
        if (beaconSensitivity !== currentSensitivity) {
            updateZoneRef.current(currentZone, {
                ...currentZone,
                beaconSensitivity: beaconSensitivity
            } as BoundariesZoneType)
        }
    }, [beaconSensitivity]);

    if (zone) {
        const soundFiles: SoundFileDef[] = [
            {
                name: "ambient",
                title: "Ambient Sound"
            }
            ,
            {
                name: "entry",
                title: "Entry Sound"
            },
            {
                name: "exit",
                title: "Exit Sound"
            }
        ];

        const description = "The boundaries of the space where the spots are placed. " +
            "If triggers are set up for the boundaries it means that";

        return <AreaZone
            description={description}
            zone={zone}
            soundFiles={soundFiles}
            initialExpandedIndex={initialExpandedIndex}
            onExpandedIndexChange={onExpandedIndexChange}
        >
            {bleEnabled ? (
                <Box marginBottom={"16px"}>
                    <HelpTooltip content={"Sets how strong a beacon signal must be before this zone triggers. Higher values are more sensitive and react from farther away; lower values keep the trigger tighter."}>
                        <Text fontWeight={"semibold"}>Beacon sensitivity:</Text>
                    </HelpTooltip>
                    <Select
                        name={"BeaconSensitivity"}
                        value={beaconSensitivity}
                        onChange={(event) => setBeaconSensitivity(+event.target.value)}
                        size={"sm"}
                        disabled={!appContext.isDraft()}
                    >
                        <option value={1}>1 (-50) - least sensitive</option>
                        <option value={2}>2 (-55)</option>
                        <option value={3}>3 (-60)</option>
                        <option value={4}>4 (-65)</option>
                        <option value={5}>5 (-70)</option>
                        <option value={6}>6 (-75)</option>
                        <option value={7}>7 (-80)</option>
                        <option value={8}>8 (-85) - default</option>
                        <option value={9}>9 (-90)</option>
                        <option value={10}>10 (-95) - most sensitive</option>
                    </Select>
                </Box>
            ) : null}
            <ManagedCheckBox name={"triggerToActivateArea"} onChange={setTriggerToActivate}
                             defaultValue={triggerToActivate}
                             disabled={!appContext.isDraft()}
            >Must be triggered to activate the space</ManagedCheckBox>
        </AreaZone>
    } else {
        return <></>
    }
}
export default BoundariesZone
